import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, map, of, tap } from 'rxjs';
import { AuthService } from './auth.service';
import { Preferito, PreferitiService } from './preferiti.service';

@Injectable({ providedIn: 'root' })
export class PreferitiStateService {
  private authService = inject(AuthService);
  private preferitiService = inject(PreferitiService);


  private preferitiSubject = new BehaviorSubject<Preferito[]>([]);
  readonly preferiti$ = this.preferitiSubject.asObservable();

  /**
   * Falso mentre è in corso il recupero dal server: i preferiti arrivano sempre
   * via API, quindi senza questa informazione la vista mostra "lista vuota" a
   * ogni ingresso nella pagina, prima che i dati arrivino.
   */
  private loadedSubject = new BehaviorSubject<boolean>(false);
  readonly loaded$ = this.loadedSubject.asObservable();

  clear(): void {
    this.preferitiSubject.next([]);
    this.loadedSubject.next(true);
  }

  get preferiti(): Preferito[] {
    return this.preferitiSubject.getValue();
  }

  isPreferito(id: number, tipo: 'testata' | 'edizione'): boolean {
    return this.preferiti.some((p) =>
      tipo === 'testata' ? p.idTestata === id : p.idEdizione === id,
    );
  }

  loadFromApi(): void {
    this.loadedSubject.next(false);
    forkJoin({
      testate: this.preferitiService.getPreferiti(),
      edizioni: this.preferitiService.getEdizioniPreferite(),
    }).subscribe({
      next: ({ testate, edizioni }) => {
        const mappedTestate: Preferito[] = (testate.data ?? []).map((item: any) => ({
          tipo: 'testata' as const,
          idRiferimento: item.idTestata ?? item.idRiferimento ?? 0,
          idTestata: item.idTestata,
          nomeTestata: item.nomeTestata ?? item.nome ?? undefined,
          dataCreazione: item.dataCreazione ?? item.dataCreazioneTestata ?? '',
          id: item.idTestata ?? item.idRiferimento ?? 0,
          nomeRiferimento: item.nomeTestata ?? item.nome ?? '',
        } as any));

        const mappedEdizioni: Preferito[] = (edizioni.data ?? []).map((item: any) => ({
          tipo: 'edizione' as const,
          idRiferimento: item.idEdizione ?? item.idRiferimento ?? 0,
          idEdizione: item.idEdizione,
          nomeEdizione: item.nomeEdizione ?? item.nome ?? undefined,
          nomeTestata: item.nomeTestata ?? undefined,
          dataCreazione: item.dataCreazione ?? '',
          id: item.idEdizione ?? item.idRiferimento ?? 0,
          nomeRiferimento: item.nomeEdizione ?? item.nome ?? '',
        } as any));

        this.preferitiSubject.next([...mappedTestate, ...mappedEdizioni]);
        this.loadedSubject.next(true);
      },
      error: () => {
        this.preferitiSubject.next([]);
        this.loadedSubject.next(true);
      },
    });
  }

  addPreferitoTestata(idTestata: number, nomeTestata?: string): Observable<void> {
    return this.preferitiService
      .postPrefTestata({  idTestata, azione: 'A' })
      .pipe(
        tap(() => {
          const entry = {
            tipo: 'testata' as const,
            idRiferimento: idTestata,
            idTestata,
            nomeTestata,
            dataCreazione: new Date().toISOString(),
            id: idTestata,
            nomeRiferimento: nomeTestata ?? '',
          } as Preferito & { id: number; nomeRiferimento: string };
          this.preferitiSubject.next([...this.preferiti, entry]);
        }),
        map(() => undefined),
      );
  }

  addPreferitoEdizione(idEdizione: number, nomeEdizione?: string): Observable<void> {
    return this.preferitiService
      .postPrefEdizione({ idEdizione, azione: 'A' })
      .pipe(
        tap(() => {
          const entry = {
            tipo: 'edizione' as const,
            idRiferimento: idEdizione,
            idEdizione,
            nomeEdizione,
            nomeTestata: nomeEdizione,
            dataCreazione: new Date().toISOString(),
            id: idEdizione,
            nomeRiferimento: nomeEdizione ?? '',
          } as Preferito & { id: number; nomeRiferimento: string };
          this.preferitiSubject.next([...this.preferiti, entry]);
        }),
        map(() => undefined),
      );
  }

  removePreferito(preferito: Preferito): Observable<void> {
    if (preferito.tipo === 'testata') {
      const idTestata = preferito.idTestata ?? preferito.idRiferimento;
      return this.preferitiService
        .postPrefTestata({  idTestata, azione: 'D' })
        .pipe(
          tap(() =>
            this.preferitiSubject.next(
              this.preferiti.filter(
                (p) => !(p.tipo === 'testata' && p.idTestata === idTestata),
              ),
            ),
          ),
          map(() => undefined),
        );
    }

    if (preferito.tipo === 'edizione') {
      const idEdizione = preferito.idEdizione ?? preferito.idRiferimento;
      return this.preferitiService
        .postPrefEdizione({  idEdizione, azione: 'D' })
        .pipe(
          tap(() =>
            this.preferitiSubject.next(
              this.preferiti.filter(
                (p) => !(p.tipo === 'edizione' && p.idEdizione === idEdizione),
              ),
            ),
          ),
          map(() => undefined),
        );
    }

    return of(undefined);
  }
}
